import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Scan, ZoomIn, ZoomOut } from 'lucide-react';
import type {
  BoardCalibration,
  BoardExtraHoldMap,
  BoardHoldMap,
  BoardHoldRadii,
  BoardShapes,
} from '../../../types/device';
import type { BoardHold } from '../../../types/climb';
import { boardRingColor, cellUses, holdHands } from './roles';
import { HandMark } from '../HandMark';
import {
  holdKey,
  holdPosition,
  holdSpecFor,
  holdWidth,
  isScannedHold,
  outlineFrame,
  scannedOutline,
  wallHoldWidth,
  type ScannedWall,
} from './geometry';
import { ScannedHoldOutlines, type ScannedHoldOutline } from './ScannedHoldOutlines';
import { ZoomResetButton } from '../photo-editor/ZoomResetButton';
import { useZoomPan } from './useZoomPan';

interface BoardGridDisplayProps {
  imageUrl: string;
  rows: number;
  columns: number;
  calibration: BoardCalibration | null;
  holds: BoardHold[];
  /**
   * The wall's own holds, so a foot chip is drawn at its size rather than a
   * bolt-on's. Empty for a plain grid, where every hold is the same size.
   */
  boardHolds?: BoardHoldMap | null;
  /** Where this wall's off-grid holds sit, so a problem using one draws it. */
  extraHolds?: BoardExtraHoldMap | null;
  /**
   * The lines a drawn wall's holds are strung along. Without them a hold of
   * such a wall has no position at all: its row is which line it is on.
   */
  shapes?: BoardShapes | null;
  /** A wall described by its photo's scan: its holds are drawn as traced. */
  scanned?: ScannedWall | null;
  holdRadii?: BoardHoldRadii | null;
  /** Circuit: the holds are numbered, in the order they are climbed. */
  circuit?: boolean;
  /**
   * Index of the hold a circuit run is on. The rest dim down to it, and it
   * pulses in time with the wall — unless it is a start hold, which is where
   * the climber already is rather than where they are going.
   */
  currentHold?: number | null;
  /**
   * The colour the wall lights that hold in (see roles.ts#circuitHighlightColor).
   * Null keeps it in its role colour — which is all a Moonboard can do.
   */
  highlightColor?: string | null;
  /**
   * Lets the board be pinched, dragged and wheeled about inside its frame, with
   * the controls a mouse needs floating over it. Off by default: the panels that
   * show a board as a thumbnail have nothing to look closer at.
   */
  zoomable?: boolean;
  /**
   * How a zoomable board offers its zoom: `buttons` floats in, out and reset
   * down its left edge (a phone, where nothing else says it can be pinched);
   * `reset` leaves the zoom to the wheel and the fingers and only offers the way
   * back out, in the corner, once there is one — as the setting canvas does.
   */
  zoomControls?: 'buttons' | 'reset';
  /**
   * Reports the picture's intrinsic size once it loads. A caller that has to
   * bound the board's height needs its aspect ratio to do it — the board is
   * laid out by its width (see below), so height is not something it can set.
   */
  onNaturalSize?: (size: { width: number; height: number }) => void;
  className?: string;
}

/** How much of itself a hold keeps while a run points somewhere else. */
const DIMMED_OPACITY = 0.3;

/** How thick a hold's ring is drawn — `border-2` below. */
const DISPLAY_RING_PX = 2;

/** Same language as the other buttons floating on a cover photo. */
const CONTROL =
  'flex items-center justify-center rounded-full bg-black/50 p-1.5 text-white transition-colors hover:bg-black/70 disabled:opacity-40 disabled:hover:bg-black/50';

/**
 * Read-only board image with the climb's selected holds drawn on top — the
 * viewer counterpart of BoardGridSelector, used on the climb profile.
 */
export default function BoardGridDisplay({
  imageUrl,
  rows,
  columns,
  calibration,
  holds,
  boardHolds,
  extraHolds,
  shapes,
  scanned = null,
  holdRadii,
  circuit = false,
  currentHold = null,
  highlightColor = null,
  zoomable = false,
  zoomControls = 'buttons',
  onNaturalSize,
  className,
}: BoardGridDisplayProps) {
  const { t } = useTranslation();
  // A mapped layout's foot chips are a few pixels across at this width, which is
  // as true of reading a problem off the board as it is of setting one.
  const zoom = useZoomPan();
  // Width over height of the photo: a hold on a plain grid is drawn at the size
  // that grid leaves for one, which cannot be worked out without it. Measured
  // here as well as handed to onNaturalSize, so a caller that does not need it
  // still gets holds the same size the setter placed them at.
  const [aspect, setAspect] = useState<number | null>(null);

  const gridWidth = useMemo(
    () => wallHoldWidth(calibration, rows, columns, aspect, shapes),
    [calibration, rows, columns, aspect, shapes]
  );

  // One circle per cell, not per hold: a circuit may come back to the same one,
  // and there is only ever one hold on the wall to draw. It wears both of its
  // numbers — see cellUses.
  const uses = useMemo(() => [...cellUses(holds).values()], [holds]);

  // The traced holds of a scanned wall, drawn under the dots that carry their
  // numbers and hand marks.
  const outlines: ScannedHoldOutline[] = [];

  const dots = uses.map(({ hold: drawn, label, indexes, standing }) => {
    const traced = isScannedHold(drawn.row) ? scannedOutline(scanned, drawn.col) : null;
    const point = traced
      ? outlineFrame(traced).centre
      : holdPosition(calibration, rows, columns, extraHolds, drawn.row, drawn.col, shapes, scanned);
    const spec = holdSpecFor(boardHolds, drawn);
    const width = traced ? Math.max(outlineFrame(traced).width, 0.01) : holdWidth(spec, holdRadii, gridWidth);
    const on = currentHold !== null && indexes.includes(currentHold);
    // A cell the run is standing on is drawn as *that* use of it, whatever it
    // is drawn as the rest of the time: the climber is being pointed at one
    // step of the circuit, not at everything the hold is for.
    const hold = on ? holds[currentHold] : drawn;
    // The hold the run is heading for takes the wall's highlight colour, so
    // the photo and the LEDs point at the same place. A start hold keeps its
    // own: the climber is already on it, and the wall does not flash it.
    const lit = on && hold.role !== 'start' && highlightColor;
    // The ring and the fill say what the hold is, or what the wall lights it in
    // — the highlight, or on a circuit that forces hands the hand that takes it
    // (see boardRingColor). That hand is marked on its own side of the ring as
    // well (see HandMark); not while the wall is lighting it, when the whole
    // hold is the highlight.
    const ring = lit ? highlightColor : boardRingColor(hold);
    const hands = lit ? [] : holdHands(hold);
    const faded = !(currentHold === null || on || standing);
    if (traced) outlines.push({ key: holdKey(drawn), outline: traced, color: ring, dimmed: faded });
    return (
      <span
        key={holdKey(drawn)}
        className={`absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 ${
          on && hold.role !== 'start' ? 'animate-pulse' : ''
        }`}
        data-hands={hands.length > 0 ? hands.join(',') : undefined}
        style={{
          left: `${point.x * 100}%`,
          top: `${point.y * 100}%`,
          width: `${width * 100}%`,
          aspectRatio: '1 / 1',
          // The outline is the hold on a scanned wall; this only carries its
          // number and hand marks.
          borderColor: traced ? 'transparent' : ring,
          backgroundColor: traced ? 'transparent' : `${ring}${on ? 'cc' : '55'}`,
          // A cell the problem stands on is shown for the whole of the run, as
          // the wall keeps it lit — the climber's foot is there throughout,
          // wherever their hands have got to.
          opacity: faded ? DIMMED_OPACITY : 1,
        }}
      >
        <HandMark hands={hands} ring={DISPLAY_RING_PX} />

        {/* A circuit's holds are stored in the order they are climbed. A hold
            the circuit comes back to — or matches on, which is the same hold
            twice over — wears every place it has in the run ("5,20"), with the
            type stepping down to keep them inside the hold. A foot has no
            number to wear: the run passes over it rather than stopping on it.
            Nor has a foot chip the room for one, whatever it is used as. */}
        {circuit && label && spec?.kind !== 'foot' && (
          <span
            className={`whitespace-nowrap font-bold leading-none text-white ${
              label.length > 2 ? 'text-[7px]' : 'text-[9px]'
            }`}
            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}
          >
            {label}
          </span>
        )}
      </span>
    );
  });

  return (
    <div className={`relative w-full overflow-hidden bg-muted ${className ?? ''}`}>
      {/* The frame the picture is pinched about. The controls stay outside it:
          it captures the pointer for the length of a gesture, which would send
          a press on a button inside it to the frame instead. */}
      <div
        {...(zoomable ? zoom.frameProps : {})}
        // Zoomed in, a sideways drag is a pan across the board; the climb
        // profile's swipe to the next climb has to let this one go. At rest
        // there is nothing to pan and the swipe is still the useful reading.
        {...(zoomable && zoom.zoomed ? { 'data-swipe-ignore': true } : {})}
        className={`relative w-full overflow-hidden ${zoomable ? 'select-none' : ''}`}
      >
        {/* The image is rendered at its natural aspect ratio (width 100%, height
            auto) so the overlay shares the image's coordinate space — the same
            setup as the creation grid. object-contain letterboxing would desync
            the dots. Zoomed, one transform moves the photo and the holds
            together, for the same reason. */}
        <div {...(zoomable ? zoom.layerProps : {})}>
          <img
            src={imageUrl}
            alt=""
            className="block w-full"
            draggable={false}
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.currentTarget;
              setAspect(naturalWidth / naturalHeight || null);
              onNaturalSize?.({ width: naturalWidth, height: naturalHeight });
            }}
          />
          <ScannedHoldOutlines holds={outlines} />
          {dots}
        </div>
      </div>

      {/* Down the left edge: the free side of a board photo, clear of the name
          above it and the rating below. Out and reset stay put but dead until
          there is something to undo, so the stack does not jump about. */}
      {zoomable && zoomControls === 'reset' && (
        <ZoomResetButton zoom={zoom} label={t('climbs.board.zoom.reset')} />
      )}

      {zoomable && zoomControls === 'buttons' && (
        <div className="absolute left-2 top-1/2 flex -translate-y-1/2 flex-col gap-1">
          <button
            type="button"
            onClick={zoom.zoomIn}
            disabled={!zoom.canZoomIn}
            title={t('climbs.board.zoom.in')}
            aria-label={t('climbs.board.zoom.in')}
            className={CONTROL}
          >
            <ZoomIn className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={zoom.zoomOut}
            disabled={!zoom.zoomed}
            title={t('climbs.board.zoom.out')}
            aria-label={t('climbs.board.zoom.out')}
            className={CONTROL}
          >
            <ZoomOut className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={zoom.reset}
            disabled={!zoom.zoomed}
            title={t('climbs.board.zoom.reset')}
            aria-label={t('climbs.board.zoom.reset')}
            className={CONTROL}
          >
            <Scan className="size-4" aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}
